import { ChangeDetectionStrategy, Component, effect, inject, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Select } from 'primeng/select';
import { Site, SiteService } from '@wheere/api-site';
import { lastValueFrom } from 'rxjs';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { toSignal } from '@angular/core/rxjs-interop';

interface SiteOption {
  value: string | null;
  label: string;
  type?: string;
}

/**
 * Generic site picker. Loads sites scoped to {@code organizationId} via the V7
 * {@code GET /sites/by-organization/{id}} endpoint, and emits the chosen site id (or
 * {@code null} for "no site").
 *
 * <p>Used by tracker-detail and gateway-detail assignment tabs in the integrator. Replaces
 * the old "fetch all sites + filter client-side" pattern that leaked cross-tenant data —
 * scoping happens server-side via the org-resolved endpoint.
 *
 * <p>For ORG_* users the backend ignores any forged {@code organizationId} and uses the JWT
 * org. For PLATFORM_* users the parent decides which org the picker is scoped to (e.g. the
 * tracker's owning org).
 */
@Component({
  selector: 'lib-site-picker',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, Select, TranslocoModule],
  template: `
    <div class="flex flex-col gap-2">
      <label class="text-xs font-semibold uppercase tracking-wider text-gray-500">
        {{ 'sitePicker.label' | transloco }}
      </label>
      <p-select
        [options]="options()"
        [ngModel]="selectedSiteId()"
        (ngModelChange)="onChange($event)"
        optionLabel="label"
        optionValue="value"
        [filter]="true"
        filterBy="label"
        appendTo="body"
        [placeholder]="'sitePicker.placeholder' | transloco"
        [showClear]="true"
        [style]="{ width: '100%' }">
      </p-select>
      @if (loading()) {
        <p class="text-xs text-gray-500">{{ 'sitePicker.loading' | transloco }}</p>
      } @else if (sites().length === 0 && !!organizationId()) {
        <p class="text-warmYellow-700 text-xs">{{ 'sitePicker.empty' | transloco }}</p>
      } @else if (!organizationId()) {
        <p class="text-xs text-gray-500">{{ 'sitePicker.waitingOrg' | transloco }}</p>
      }
    </div>
  `,
})
export class SitePickerComponent {
  /**
   * Org id to scope the picker to. {@code null} disables the load (e.g. parent doesn't know
   * the org yet — the legacy "all sites" path is intentionally NOT used to avoid cross-tenant
   * leaks).
   */
  organizationId = input<string | null>(null);

  /** Initial selection — bound on first change of {@link #organizationId}. */
  initialSiteId = input<string | null>(null);

  selected = output<string | null>();

  protected readonly sites = signal<Site[]>([]);
  protected readonly loading = signal(false);
  protected readonly selectedSiteId = signal<string | null>(null);

  /** PrimeNG select options — `null` value is the "— No site —" entry. */
  protected readonly options = signal<SiteOption[]>([{ value: null, label: '— No site —' }]);

  #siteService = inject(SiteService);
  #transloco = inject(TranslocoService);
  // Re-build option labels whenever the active language changes.
  protected readonly activeLang = toSignal(this.#transloco.langChanges$, {
    initialValue: this.#transloco.getActiveLang(),
  });

  /** Track the last loaded org so we don't refetch on unrelated input changes. */
  #lastLoadedOrgId: string | null | undefined = undefined;

  constructor() {
    // Seed the "— No site —" entry with the current language so the placeholder option
    // renders translated even before any sites are loaded.
    this.options.set([{ value: null, label: this.#transloco.translate('sitePicker.noSite') }]);

    // Auto-load whenever the org id changes. `effect` runs eagerly (unlike `computed` which is
    // lazy and only runs when something reads it — that was the bug: the previous version used
    // `computed(() => { … this.load(orgId); … })` and nothing ever read it, so the picker
    // stayed empty even when the parent had the org id ready.
    effect(
      () => {
        const orgId = this.organizationId();
        if (orgId === this.#lastLoadedOrgId) return;
        this.#lastLoadedOrgId = orgId;
        void this.load(orgId);
      },
      { allowSignalWrites: true }
    );

    // Sync initial selection whenever it lands.
    effect(
      () => {
        const init = this.initialSiteId();
        if (init !== null && init !== this.selectedSiteId()) {
          this.selectedSiteId.set(init);
        }
      },
      { allowSignalWrites: true }
    );

    // Re-translate the static "— No site —" placeholder on language change.
    effect(
      () => {
        this.activeLang();
        const noSiteLabel = this.#transloco.translate('sitePicker.noSite');
        this.options.update(opts => {
          if (opts.length === 0) return [{ value: null, label: noSiteLabel }];
          // Replace just the leading null-value option, preserving any loaded sites after it.
          const [, ...rest] = opts;
          return [{ value: null, label: noSiteLabel }, ...rest];
        });
      },
      { allowSignalWrites: true }
    );
  }

  protected onChange(siteId: string | null): void {
    this.selectedSiteId.set(siteId);
    this.selected.emit(siteId);
  }

  private async load(orgId: string | null): Promise<void> {
    const noSiteLabel = this.#transloco.translate('sitePicker.noSite');
    if (!orgId) {
      this.sites.set([]);
      this.options.set([{ value: null, label: noSiteLabel }]);
      return;
    }
    this.loading.set(true);
    try {
      const list = await lastValueFrom(this.#siteService.getSitesByOrganization(orgId));
      this.sites.set(list);
      this.options.set([
        { value: null, label: noSiteLabel },
        ...list.map(s => ({
          value: s.id ?? '',
          label: s.name + (s.type ? ' · ' + s.type : ''),
          type: s.type,
        })),
      ]);
    } catch {
      this.sites.set([]);
      this.options.set([{ value: null, label: noSiteLabel }]);
    } finally {
      this.loading.set(false);
    }
  }
}
